import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { FiFeather, FiSearch, FiStar, FiChevronDown } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import {
  MdLocalFireDepartment,
  MdClear,
  MdBrandingWatermark,
  MdCategory,
  MdOutlineDiscount,
} from "react-icons/md";
import {
  FaGift,
  FaRupeeSign,
  FaChevronLeft,
  FaChevronRight,
  FaLeaf,
  FaGem,
  FaClock,
  FaRegHeart,
  FaEye,
} from "react-icons/fa";
import { BiRupee } from "react-icons/bi";
import api from "../services/api";
import "../styles/CollectionPage.css";
import { BASE_URL } from "../config";

/* ─── Constants ─────────────────────────────────────────────────── */

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "name-asc", label: "Name: A–Z" },
  { value: "name-desc", label: "Name: Z–A" },
  { value: "newest", label: "Newest First" },
  { value: "popularity", label: "Most Popular" },
  { value: "rating", label: "Top Rated" },
];

const BANNER_IMAGES = {
  Lungies: "/assets/Banner/Banner-Lungi-1.png",
  Bedsheets: "/assets/Banner/Bedsheet1.png",
  Kaavi: "/assets/Banner/Banner-kaavii.png",
  Towel: "/assets/Banner/Banner-Towel1.png",
  Hometextiles: "/assets/Banner/hometext.jpg",
  default: "/assets/Banner/Banner-Lungi2.png",
};

const MOOD_OPTIONS = [
  {
    id: "soft",
    icon: <FiFeather />,
    label: "Soft & Cozy",
    description: "Light, breathable",
    tags: ["cotton", "soft"],
    maxPrice: 800,
    color: "#c9a87c",
    bg: "#faf5ec",
  },
  {
    id: "bold",
    icon: <MdLocalFireDepartment />,
    label: "Bold & Bright",
    description: "Vivid statement",
    tags: ["coloured", "printed"],
    maxPrice: "",
    color: "#c4603a",
    bg: "#fdf0eb",
  },
  {
    id: "gifting",
    icon: <FaGift />,
    label: "Gift-Ready",
    description: "Premium finish",
    tags: ["premium", "gift"],
    minPrice: 500,
    color: "#8e6baa",
    bg: "#f5f0f8",
  },
  {
    id: "budget",
    icon: <BiRupee />,
    label: "Best Value",
    description: "Under ₹500",
    tags: [],
    maxPrice: 500,
    color: "#4a8c68",
    bg: "#eef6f1",
  },
  {
    id: "eco",
    icon: <FaLeaf />,
    label: "Eco-Friendly",
    description: "Sustainable",
    tags: ["organic", "eco"],
    color: "#5a8c4a",
    bg: "#eef5eb",
  },
  {
    id: "luxury",
    icon: <FaGem />,
    label: "Luxury",
    description: "Opulent threads",
    tags: ["luxury", "premium"],
    minPrice: 1200,
    color: "#b8922a",
    bg: "#fdf5e6",
  },
];

const COLOR_OPTIONS = [
  { name: "Red", value: "red", hex: "#c0392b" },
  { name: "Blue", value: "blue", hex: "#2980b9" },
  { name: "Green", value: "green", hex: "#27ae60" },
  { name: "Yellow", value: "yellow", hex: "#d4a017" },
  { name: "Purple", value: "purple", hex: "#8e44ad" },
  { name: "Pink", value: "pink", hex: "#c0306a" },
  { name: "Orange", value: "orange", hex: "#d35400" },
  { name: "Black", value: "black", hex: "#1a1a1a" },
  { name: "White", value: "white", hex: "#e8e0d4", border: true },
  { name: "Brown", value: "brown", hex: "#6d4c41" },
];

const DISCOUNT_RANGES = [
  { label: "10% and above", min: 10 },
  { label: "20% and above", min: 20 },
  { label: "30% and above", min: 30 },
  { label: "50% and above", min: 50 },
];

const RATING_FILTERS = [4, 3, 2];
const ITEMS_PER_PAGE = 12;
const MAX_RECENT = 6;
const HISTOGRAM_BUCKETS = [2, 5, 9, 14, 18, 22, 16, 11, 7, 4];

/* ─── Session helpers ──────────────────────────────────────────── */
function getRecentlyViewed() {
  try {
    return JSON.parse(sessionStorage.getItem("rv_products") || "[]");
  } catch {
    return [];
  }
}
function addRecentlyViewed(product) {
  if (!product?._id) return;
  const prev = getRecentlyViewed().filter((p) => p._id !== product._id);
  sessionStorage.setItem(
    "rv_products",
    JSON.stringify([product, ...prev].slice(0, MAX_RECENT)),
  );
}
function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem("wishlist") || "[]");
  } catch {
    return [];
  }
}
function toggleWishlist(productId) {
  const current = getWishlist();
  const updated = current.includes(productId)
    ? current.filter((id) => id !== productId)
    : [...current, productId];
  localStorage.setItem("wishlist", JSON.stringify(updated));
  return updated;
}

/* ─── Filter Dropdown Component ─────────────────────────────────── */
function FilterDropdown({ label, activeCount = 0, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={ref}
      className={`fd-root ${open ? "fd-open" : ""} ${activeCount > 0 ? "fd-active" : ""}`}
    >
      <button className="fd-trigger" onClick={() => setOpen((o) => !o)}>
        <span className="fd-label">{label}</span>
        {activeCount > 0 && <span className="fd-badge">{activeCount}</span>}
        <FiChevronDown
          className={`fd-chevron ${open ? "fd-chevron-up" : ""}`}
          size={12}
        />
      </button>
      {open && <div className="fd-panel">{children}</div>}
    </div>
  );
}

/* ─── Price Histogram ──────────────────────────────────────────── */
function PriceHistogram({
  buckets,
  priceMin,
  priceMax,
  minBound = 0,
  maxBound = 5000,
}) {
  const maxCount = Math.max(...buckets, 1);
  return (
    <div className="ph-root">
      <div className="ph-bars">
        {buckets.map((count, i) => {
          const bMin = minBound + (i / buckets.length) * (maxBound - minBound);
          const bMax =
            minBound + ((i + 1) / buckets.length) * (maxBound - minBound);
          const inRange =
            (!priceMin || bMax >= Number(priceMin)) &&
            (!priceMax || bMin <= Number(priceMax));
          return (
            <div
              key={i}
              className={`ph-bar ${inRange ? "active" : ""}`}
              style={{ height: `${(count / maxCount) * 38}px` }}
              title={`₹${Math.round(bMin)}–₹${Math.round(bMax)}: ${count} items`}
            />
          );
        })}
      </div>
      <div className="ph-labels">
        <span>₹{minBound}</span>
        <span>₹{maxBound.toLocaleString()}</span>
      </div>
    </div>
  );
}

/* ─── Active Filter Chips ───────────────────────────────────────── */
function ActiveFilters({
  checkedBrands,
  checkedCats,
  checkedColors,
  checkedProductNames,
  discountFilter,
  ratingFilter,
  priceMin,
  priceMax,
  activeMood,
  onRemoveBrand,
  onRemoveCat,
  onRemoveColor,
  onRemoveProductName,
  onClearDiscount,
  onClearRating,
  onClearPrice,
  onClearMood,
  onClearAll,
}) {
  const chips = [
    ...checkedCats.map((c) => ({
      id: `cat:${c}`,
      label: c,
      onRemove: () => onRemoveCat(c),
    })),
    ...checkedBrands.map((b) => ({
      id: `brand:${b}`,
      label: b,
      onRemove: () => onRemoveBrand(b),
    })),
    ...checkedColors.map((c) => ({
      id: `color:${c}`,
      label: c,
      onRemove: () => onRemoveColor(c),
    })),
    ...checkedProductNames.map((p) => ({
      id: `product:${p}`,
      label: p,
      onRemove: () => onRemoveProductName(p),
    })),
    ...(discountFilter
      ? [
        {
          id: "disc",
          label: `${discountFilter}%+ off`,
          onRemove: onClearDiscount,
        },
      ]
      : []),
    ...(ratingFilter
      ? [{ id: "rate", label: `${ratingFilter}★+`, onRemove: onClearRating }]
      : []),
    ...(priceMin || priceMax
      ? [
        {
          id: "price",
          label: `₹${priceMin || "0"}–₹${priceMax || "∞"}`,
          onRemove: onClearPrice,
        },
      ]
      : []),
    ...(activeMood
      ? [
        {
          id: "mood",
          label: MOOD_OPTIONS.find((m) => m.id === activeMood)?.label,
          onRemove: onClearMood,
        },
      ]
      : []),
  ];
  if (!chips.length) return null;
  return (
    <div className="af-strip">
      <span className="af-label">Active</span>
      <div className="af-chips">
        {chips.map((c) => (
          <button key={c.id} className="af-chip" onClick={c.onRemove}>
            {c.label}
            <span className="af-x">×</span>
          </button>
        ))}
      </div>
      <button className="af-clear" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

/* ─── Recently Viewed Rail ──────────────────────────────────────── */
function RecentlyViewedRail({ products, onNavigate }) {
  if (!products.length) return null;
  return (
    <section className="rv-section">
      <div className="rv-header">
        <h3 className="rv-title">
          <FaClock />
          &ensp;Recently Viewed
        </h3>
        <span className="rv-hint">Continue browsing</span>
      </div>
      <div className="rv-track">
        {products.map((p) => (
          <article
            key={p._id}
            className="rv-card"
            onClick={() => onNavigate(p)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onNavigate(p)}
          >
            <div className="rv-img-wrap">
              <img
                src={`${BASE_URL}${p.images?.[0] || p.image}`}
                alt={p.name}
                loading="lazy"
                onError={(e) => {
                  e.target.src = `https://placehold.co/300x400/f0ebe3/999?text=${encodeURIComponent(p.name?.substring(0, 6) || "IMG")}`;
                }}
              />
              <div className="rv-overlay">
                <span>View</span>
              </div>
            </div>
            <p className="rv-name">{p.name}</p>
            <p className="rv-price">₹{p.price}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ─── Skeleton Card ─────────────────────────────────────────────── */
function SkeletonCard({ list }) {
  return (
    <div className={`skel-card ${list ? "skel-list" : ""}`}>
      <div className="skel-img" />
      <div className="skel-body">
        <div className="skel-line w60" />
        <div className="skel-line w35" />
        {list && <div className="skel-line w50" />}
      </div>
    </div>
  );
}

/* ─── Enhanced Product Card ──────────────────────────────────────── */
function EnhancedProductCard({
  product,
  listView,
  onQuickView,
  onWishlistToggle,
  isWishlisted,
}) {
  const [hovered, setHovered] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 50, y: 50 });
  const cardRef = useRef(null);
  const imgSrc = product.images?.[0] || product.image;

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    setLensPos({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  const discount = product.originalPrice
    ? Math.round(
      ((product.originalPrice - product.price) / product.originalPrice) * 100,
    )
    : 0;

  return (
    <article
      ref={cardRef}
      className={`epc ${listView ? "epc-list" : ""} ${hovered ? "epc-hovered" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {discount > 0 && <div className="epc-discount">−{discount}%</div>}
      <button
        className={`epc-wish ${isWishlisted ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onWishlistToggle(product._id);
        }}
        aria-label="Toggle wishlist"
      >
        {isWishlisted ? <FaHeart /> : <FaRegHeart />}
      </button>
      <button
        className="epc-qv"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onQuickView(product);
        }}
      >
        <FaEye size={11} /> Quick View
      </button>
      <ProductCard product={product} listView={listView} />
      {hovered && imgSrc && !listView && (
        <div
          className="epc-lens"
          style={{
            backgroundImage: `url(${BASE_URL}${imgSrc})`,
            backgroundPosition: `${lensPos.x}% ${lensPos.y}%`,
          }}
        />
      )}
    </article>
  );
}

/* ─── Quick View Modal ─────────────────────────────────────────── */
function QuickViewModal({ product, onClose, onAddToCart, onNavigate }) {
  if (!product) return null;
  const discountPercent = product.originalPrice
    ? Math.round(
      ((product.originalPrice - product.price) / product.originalPrice) * 100,
    )
    : 0;
  return (
    <div className="qv-overlay" onClick={onClose}>
      <div className="qv-modal" onClick={(e) => e.stopPropagation()}>
        <button className="qv-close" onClick={onClose} aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M1 1L11 11M11 1L1 11"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="qv-grid">
          <div className="qv-img-col">
            {discountPercent > 0 && (
              <div className="qv-badge">−{discountPercent}%</div>
            )}
            <img
              src={`${BASE_URL}${product.images?.[0] || product.image}`}
              alt={product.name}
            />
          </div>
          <div className="qv-info-col">
            <p className="qv-eyebrow">{product.category || "Collection"}</p>
            <h2 className="qv-title">{product.name}</h2>
            <div className="qv-price-row">
              <span className="qv-price-now">₹{product.price}</span>
              {product.originalPrice && (
                <>
                  <span className="qv-price-was">₹{product.originalPrice}</span>
                  <span className="qv-price-save">Save {discountPercent}%</span>
                </>
              )}
            </div>
            <div className="qv-rule" />
            <p className="qv-desc">
              {product.description?.substring(0, 180)}
              {product.description?.length > 180 ? "…" : ""}
            </p>
            <div className="qv-actions">
              <button
                className="qv-btn-primary"
                onClick={() => onAddToCart(product)}
              >
                Add to Bag
              </button>
              <button
                className="qv-btn-ghost"
                onClick={() => onNavigate(product)}
              >
                Full Details
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path
                    d="M2 5.5H9M6 2.5L9 5.5L6 8.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function CollectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { category, slug, search: locationSearch } = location.state || {};

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableProductNames, setAvailableProductNames] = useState([]);

  const [sort, setSort] = useState("featured");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [checkedBrands, setCheckedBrands] = useState([]);
  const [checkedCats, setCheckedCats] = useState([]);
  const [checkedColors, setCheckedColors] = useState([]);
  const [checkedProductNames, setCheckedProductNames] = useState([]);
  const [discountFilter, setDiscountFilter] = useState(null);
  const [ratingFilter, setRatingFilter] = useState(null);
  const [page, setPage] = useState(1);

  const [activeSubcategorySlug, setActiveSubcategorySlug] = useState(
    slug || null,
  );
  const [activeSubcategoryName, setActiveSubcategoryName] = useState("");
  const [activeMood, setActiveMood] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState(getRecentlyViewed);
  const [viewMode, setViewMode] = useState("grid");
  const [wishlist, setWishlist] = useState(getWishlist);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    api.products
      .getCategories()
      .then((d) => {
        setBrands(d.brands || []);
        setCategories(d.categories || []);
      })
      .catch(() => { });
  }, []);

  // Fixed: Don't use subcategory filtering, just show all products of the category
  useEffect(() => {
    if (slug) {
      // Just store the slug but don't use it for filtering
      setActiveSubcategorySlug(slug);
      setActiveSubcategoryName(
        slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      );
    } else {
      setActiveSubcategorySlug(null);
      setActiveSubcategoryName("");
    }
  }, [slug, category]);

  const handleMoodSelect = (moodId) => {
    setActiveMood(moodId);
    if (!moodId) {
      setPriceMin("");
      setPriceMax("");
      return;
    }
    const mood = MOOD_OPTIONS.find((m) => m.id === moodId);
    if (mood) {
      setPriceMax(mood.maxPrice !== undefined ? String(mood.maxPrice) : "");
      setPriceMin(mood.minPrice ? String(mood.minPrice) : "");
    }
  };

  const handleWishlistToggle = (productId) =>
    setWishlist(toggleWishlist(productId));

  // FIXED: Simplified fetchProducts - only use category, ignore subcategory
  const fetchProducts = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        sort,
        page,
        limit: ITEMS_PER_PAGE,
      };

      if (locationSearch) {
        params.search = locationSearch;
      }

      // Only use category filter, ignore subcategory
      if (category && category !== "all" && !checkedCats.length) {
        params.category = category;
      } else if (checkedCats.length) {
        params.category = checkedCats.join(",");
      }

      // DO NOT add subcategory filter - show all products in the category

      if (checkedBrands.length) {
        params.brand = checkedBrands.join(",");
      }

      if (priceMin) {
        params.minPrice = priceMin;
      }
      if (priceMax) {
        params.maxPrice = priceMax;
      }

      if (discountFilter) {
        params.minDiscount = discountFilter;
      }

      if (ratingFilter) {
        params.minRating = ratingFilter;
      }

      const data = await api.products.getAll(params);

      let filtered = data.products || [];

      // Apply client-side color filtering
      if (checkedColors.length) {
        filtered = filtered.filter((p) =>
          p.colors?.some((c) => checkedColors.includes(c.toLowerCase())),
        );
      }

      // Apply client-side product name filtering
      if (checkedProductNames.length) {
        filtered = filtered.filter((p) =>
          checkedProductNames.includes(p.name),
        );
      }

      // Extract unique product names for the filter dropdown
      const uniqueNames = [...new Set(filtered.map(p => p.name))];
      setAvailableProductNames(uniqueNames);

      setProducts(filtered);
      setTotal(data.total || filtered.length);

      if (data.products?.[0]) {
        addRecentlyViewed(data.products[0]);
        setRecentlyViewed(getRecentlyViewed());
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotal(0);
      setAvailableProductNames([]);
    } finally {
      setLoading(false);
    }
  }, [
    sort,
    page,
    locationSearch,
    category,
    checkedBrands,
    checkedCats,
    checkedColors,
    checkedProductNames,
    priceMin,
    priceMax,
    discountFilter,
    ratingFilter,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    sort,
    locationSearch,
    category,
    checkedBrands,
    checkedCats,
    checkedColors,
    checkedProductNames,
    priceMin,
    priceMax,
    discountFilter,
    ratingFilter,
    activeMood,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const toggleBrand = (b) =>
    setCheckedBrands((p) =>
      p.includes(b) ? p.filter((x) => x !== b) : [...p, b],
    );
  const toggleCat = (c) =>
    setCheckedCats((p) =>
      p.includes(c) ? p.filter((x) => x !== c) : [...p, c],
    );
  const toggleColor = (c) =>
    setCheckedColors((p) =>
      p.includes(c) ? p.filter((x) => x !== c) : [...p, c],
    );
  const toggleProductName = (name) =>
    setCheckedProductNames((p) =>
      p.includes(name) ? p.filter((x) => x !== name) : [...p, name],
    );

  const clearAll = () => {
    setCheckedBrands([]);
    setCheckedCats([]);
    setCheckedColors([]);
    setCheckedProductNames([]);
    setPriceMin("");
    setPriceMax("");
    setActiveMood(null);
    setDiscountFilter(null);
    setRatingFilter(null);
    setPage(1);
  };

  const displayTitle = (() => {
    if (locationSearch) return `"${locationSearch}"`;
    if (activeSubcategoryName) return activeSubcategoryName;
    return category || "All Products";
  })();

  const activeFilterCount = [
    checkedBrands.length,
    checkedCats.length,
    checkedColors.length,
    checkedProductNames.length,
    priceMin || priceMax ? 1 : 0,
    activeMood ? 1 : 0,
    discountFilter ? 1 : 0,
    ratingFilter ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const hasActiveFilters = activeFilterCount > 0;
  const isListView = viewMode === "list";

  return (
    <div className="cp-root">
      {/* ── Hero ── */}
      <header className="cp-hero">
        <div
          className="cp-hero-bg"
          style={{
            backgroundImage: `url(${BANNER_IMAGES[category] || BANNER_IMAGES.default})`,
          }}
        />

        <div className="cp-hero-veil" />
        <div className="cp-hero-body">
          <p className="cp-hero-eyebrow">
            {locationSearch
              ? "Search Results"
              : category
                ? `${category} Collection`
                : "Our Collection"}
          </p>
          <h1 className="cp-hero-title">{displayTitle}</h1>
          <div className="cp-hero-meta">
            <span className="cp-hero-count">
              {loading ? "Curating…" : `${total.toLocaleString()} pieces`}
            </span>
            <span className="cp-hero-dot" />
            <span className="cp-hero-sub">Handcrafted in India</span>
          </div>
        </div>
        <div className="cp-hero-scroll-cue">
          <span />
        </div>
      </header>

      {/* ══════════════════════════════════════════
          FILTER TOPBAR — horizontal dropdown bar
      ══════════════════════════════════════════ */}
      <div className="filter-topbar">
        <div className="filter-topbar-row">
          {/* Left side: filter dropdown pills */}
          <div className="filter-topbar-left">
            {categories.length > 0 && (
              <FilterDropdown label="Category" activeCount={checkedCats.length}>
                <div className="fd-body">
                  {categories.map((c) => (
                    <label key={c} className="fd-option">
                      <input
                        type="checkbox"
                        checked={checkedCats.includes(c)}
                        onChange={() => toggleCat(c)}
                      />
                      <span className="fd-check" />
                      <span className="fd-opt-label">{c}</span>
                    </label>
                  ))}
                </div>
              </FilterDropdown>
            )}

            {/* Product Name Filter */}
            {availableProductNames.length > 0 && (
              <FilterDropdown
                label="Product Name"
                activeCount={checkedProductNames.length}
              >
                <div className="fd-body">
                  <div className="fd-search">
                    <input
                      type="text"
                      placeholder="Search products..."
                      className="fd-search-input"
                      onChange={(e) => {
                        const searchTerm = e.target.value.toLowerCase();
                        const filtered = availableProductNames.filter(name =>
                          name.toLowerCase().includes(searchTerm)
                        );
                        // You can implement search functionality here
                      }}
                    />
                  </div>
                  <div className="fd-name-list">
                    {availableProductNames.map((name) => (
                      <label key={name} className="fd-option">
                        <input
                          type="checkbox"
                          checked={checkedProductNames.includes(name)}
                          onChange={() => toggleProductName(name)}
                        />
                        <span className="fd-check" />
                        <span className="fd-opt-label fd-product-name">
                          {name.length > 40 ? name.substring(0, 40) + "..." : name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </FilterDropdown>
            )}

            <FilterDropdown
              label="Price"
              activeCount={priceMin || priceMax ? 1 : 0}
            >
              <div className="fd-body fd-body-price">
                <PriceHistogram
                  buckets={HISTOGRAM_BUCKETS}
                  priceMin={priceMin}
                  priceMax={priceMax}
                  maxBound={5000}
                />
                <div className="fd-price-row">
                  <input
                    type="number"
                    placeholder="Min ₹"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    className="fd-price-input"
                  />
                  <span className="fd-price-dash">–</span>
                  <input
                    type="number"
                    placeholder="Max ₹"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className="fd-price-input"
                  />
                </div>
              </div>
            </FilterDropdown>

            <FilterDropdown label="Colour" activeCount={checkedColors.length}>
              <div className="fd-body fd-body-colors">
                <div className="fd-swatch-grid">
                  {COLOR_OPTIONS.map((col) => (
                    <button
                      key={col.value}
                      className={`fd-swatch ${checkedColors.includes(col.value) ? "active" : ""}`}
                      style={{
                        background: col.hex,
                        boxShadow: col.border
                          ? "0 0 0 1px #d0c8be inset"
                          : "none",
                      }}
                      title={col.name}
                      onClick={() => toggleColor(col.value)}
                      aria-label={col.name}
                    />
                  ))}
                </div>
                {checkedColors.length > 0 && (
                  <div className="fd-swatch-tags">
                    {checkedColors.map((c) => (
                      <span key={c} className="fd-swatch-tag">
                        <span
                          className="fd-swatch-dot"
                          style={{
                            background: COLOR_OPTIONS.find((o) => o.value === c)
                              ?.hex,
                          }}
                        />
                        {c}
                        <button
                          className="fd-swatch-tag-x"
                          onClick={() => toggleColor(c)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </FilterDropdown>

            <FilterDropdown
              label="Discount"
              activeCount={discountFilter ? 1 : 0}
            >
              <div className="fd-body">
                {DISCOUNT_RANGES.map((d) => (
                  <label key={d.label} className="fd-option">
                    <input
                      type="radio"
                      name="discount"
                      checked={discountFilter === d.min}
                      onChange={() =>
                        setDiscountFilter(
                          discountFilter === d.min ? null : d.min,
                        )
                      }
                    />
                    <span className="fd-check fd-check-radio" />
                    <span className="fd-opt-label">{d.label}</span>
                  </label>
                ))}
              </div>
            </FilterDropdown>

            <FilterDropdown label="Rating" activeCount={ratingFilter ? 1 : 0}>
              <div className="fd-body">
                {RATING_FILTERS.map((r) => (
                  <label key={r} className="fd-option">
                    <input
                      type="radio"
                      name="rating"
                      checked={ratingFilter === r}
                      onChange={() =>
                        setRatingFilter(ratingFilter === r ? null : r)
                      }
                    />
                    <span className="fd-check fd-check-radio" />
                    <span className="fd-opt-label fd-stars">
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < r ? "star-on" : "star-off"}
                        >
                          ★
                        </span>
                      ))}
                      <span className="fd-stars-and">& above</span>
                    </span>
                  </label>
                ))}
              </div>
            </FilterDropdown>

            {brands.length > 0 && (
              <FilterDropdown label="Brand" activeCount={checkedBrands.length}>
                <div className="fd-body">
                  {brands.map((b) => (
                    <label key={b} className="fd-option">
                      <input
                        type="checkbox"
                        checked={checkedBrands.includes(b)}
                        onChange={() => toggleBrand(b)}
                      />
                      <span className="fd-check" />
                      <span className="fd-opt-label">{b}</span>
                    </label>
                  ))}
                </div>
              </FilterDropdown>
            )}

            {hasActiveFilters && (
              <button className="filter-clear-all" onClick={clearAll}>
                <MdClear size={12} />
                Clear all
              </button>
            )}
          </div>

          {/* Right side: count, view toggle, sort */}
          <div className="filter-topbar-right">
            <span className="cp-count-label">
              {loading
                ? "—"
                : `${Math.min((page - 1) * ITEMS_PER_PAGE + 1, total)}–${Math.min(page * ITEMS_PER_PAGE, total)} of ${total}`}
            </span>

            <div className="cp-view-toggle">
              <button
                className={`cp-view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="currentColor"
                >
                  <rect x="0" y="0" width="5.5" height="5.5" rx="1.5" />
                  <rect x="7.5" y="0" width="5.5" height="5.5" rx="1.5" />
                  <rect x="0" y="7.5" width="5.5" height="5.5" rx="1.5" />
                  <rect x="7.5" y="7.5" width="5.5" height="5.5" rx="1.5" />
                </svg>
              </button>
              <button
                className={`cp-view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="currentColor"
                >
                  <rect x="0" y="0" width="13" height="2.5" rx="1.25" />
                  <rect x="0" y="5" width="13" height="2.5" rx="1.25" />
                  <rect x="0" y="10" width="13" height="2.5" rx="1.25" />
                </svg>
              </button>
            </div>

            <div className="cp-sort-wrap">
              <select
                className="cp-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="cp-sort-arrow" size={12} />
            </div>
          </div>
        </div>

        {/* Active chip strip — only when filters are applied */}
        {hasActiveFilters && (
          <div className="filter-topbar-chips">
            <ActiveFilters
              checkedBrands={checkedBrands}
              checkedCats={checkedCats}
              checkedColors={checkedColors}
              checkedProductNames={checkedProductNames}
              discountFilter={discountFilter}
              ratingFilter={ratingFilter}
              priceMin={priceMin}
              priceMax={priceMax}
              activeMood={activeMood}
              onRemoveBrand={(b) =>
                setCheckedBrands((p) => p.filter((x) => x !== b))
              }
              onRemoveCat={(c) =>
                setCheckedCats((p) => p.filter((x) => x !== c))
              }
              onRemoveColor={(c) =>
                setCheckedColors((p) => p.filter((x) => x !== c))
              }
              onRemoveProductName={(n) =>
                setCheckedProductNames((p) => p.filter((x) => x !== n))
              }
              onClearDiscount={() => setDiscountFilter(null)}
              onClearRating={() => setRatingFilter(null)}
              onClearPrice={() => {
                setPriceMin("");
                setPriceMax("");
              }}
              onClearMood={() => handleMoodSelect(null)}
              onClearAll={clearAll}
            />
          </div>
        )}
      </div>

      <div
        className="viewprod"
        onClick={() => {
          navigate("/collection");
        }}
      >
        View All Products
      </div>

      {/* ── Products ── */}
      <div className="cp-content">
        {loading ? (
          <div className={`cp-grid ${isListView ? "list" : ""}`}>
            {Array.from({ length: 18 }, (_, i) => (
              <SkeletonCard key={i} list={isListView} />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className={`cp-grid ${isListView ? "list" : ""}`}>
            {products.map((p) => (
              <EnhancedProductCard
                key={p._id}
                product={p}
                listView={isListView}
                onQuickView={setQuickViewProduct}
                onWishlistToggle={handleWishlistToggle}
                isWishlisted={wishlist.includes(p._id)}
              />
            ))}
          </div>
        ) : (
          <div className="cp-empty">
            <div className="cp-empty-icon">
              <FiSearch />
            </div>
            <h3>Nothing found</h3>
            <p>Try adjusting your filters or explore a different category.</p>
            <button className="cp-empty-btn" onClick={clearAll}>
              Clear all filters
            </button>
          </div>
        )}

        {totalPages > 1 && !loading && (
          <nav className="cp-pagination" aria-label="Pagination">
            <button
              className="cp-pg-arrow"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              aria-label="Previous"
            >
              <FaChevronLeft size={10} />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pg;
              if (totalPages <= 7) pg = i + 1;
              else if (page <= 4) pg = i + 1;
              else if (page >= totalPages - 3) pg = totalPages - 6 + i;
              else pg = page - 3 + i;
              return (
                <button
                  key={pg}
                  className={`cp-pg-num ${page === pg ? "active" : ""}`}
                  onClick={() => setPage(pg)}
                  aria-current={page === pg ? "page" : undefined}
                >
                  {pg}
                </button>
              );
            })}
            <button
              className="cp-pg-arrow"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              aria-label="Next"
            >
              <FaChevronRight size={10} />
            </button>
          </nav>
        )}
      </div>

      {/* Recently Viewed */}
      <RecentlyViewedRail
        products={recentlyViewed}
        onNavigate={(p) =>
          navigate(`/product/${p._id}`, { state: { product: p } })
        }
      />

      {/* Quick View */}
      {quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          onClose={() => setQuickViewProduct(null)}
          onAddToCart={(product) => console.log("Add to cart:", product)}
          onNavigate={(product) => {
            setQuickViewProduct(null);
            navigate(`/product/${product._id}`, { state: { product } });
          }}
        />
      )}
    </div>
  );
}